import { useMutation } from '@apollo/client';
import type { UpdateDashboardMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateDashboardDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { UpdateDashboardTRPCInput } from './useUpdateDashboardTRPC';
import { useUpdateDashboardTRPC } from './useUpdateDashboardTRPC';

export const useUpdateDashboard = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateDashboardGraphQL, graphqlState] = useMutation(
    UpdateDashboardDocument,
    {
      refetchQueries: [
        namedOperations.Query.getDashboards,
        namedOperations.Query.getDashboardById,
      ],
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateDashboardTRPC();

  const updateDashboard = async (
    variables: UpdateDashboardTRPCInput
  ): Promise<UpdateDashboardMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateDashboard(variables);
    }

    const result = await updateDashboardGraphQL({
      variables: {
        Id: variables.Id,
        Name: variables.Name,
        Description: variables.Description,
        Sharing: variables.Sharing,
        Content: variables.Content,
        ContributorUserIds: variables.ContributorUserIds,
        ContributorGroupIds: variables.ContributorGroupIds,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update dashboard');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateDashboard,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateDashboard,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
